import { TenantBaseApi } from "api/TenantSlug/TenantBaseApi.ts";

import GetUsersResponse = App.Http.Responses.User.GetUsersResponse;
import GetUserResponse = App.Http.Responses.User.GetUserResponse;
import GetUserRequest = App.Http.Requests.User.GetUserRequest;
import GetUsersRequest = App.Http.Requests.User.GetUsersRequest;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import ExportResponse = App.Http.Responses.ExportResponse;
import SetCommissionGroupsToUserRequest = App.Http.Requests.User.SetCommissionGroupsToUserRequest;
import GetUserConversionsApiTokenResponse = App.Http.Responses.User.GetUserConversionsApiTokenResponse;
import StoreTenantUserRequest = App.Http.Requests.User.StoreTenantUserRequest;

class UserApi extends TenantBaseApi {
    /**
     * Get list of Users
     *
     * - Request: GetUsersRequest
     * - Response: GetUsersResponse
     */

    async index(request: GetUsersRequest): Promise<GetUsersResponse> {
        return await this.get<GetUsersResponse, GetUsersRequest>("/users", {
            params: request,
        });
    }

    /**
     * Create a new user directly within the current tenant (affiliate/advertiser "Add" drawers).
     * Always a distinct new person with a password set directly by the admin — no invitation email.
     *
     * - Request: StoreTenantUserRequest
     * - Response: GetUserResponse
     */
    async store(request: StoreTenantUserRequest): Promise<GetUserResponse> {
        return await this.post<GetUserResponse, StoreTenantUserRequest>("/users", request, { silentError: true });
    }

    /**
     * Get a specific User
     *
     * - Request: GetUserRequest
     * - Response: GetUserResponse
     */

    async show(userId: string, request: GetUserRequest): Promise<GetUserResponse> {
        return await this.get<GetUserResponse, unknown>(`/users/${userId}`, {
            params: request,
        });
    }

    async export(request: BaseExportRequest): Promise<ExportResponse> {
        return await this.post<ExportResponse, BaseExportRequest>("/users/export", request);
    }

    /**
     * Set commission groups for a user
     *
     * - Request: SetCommissionGroupsToUserRequest
     */
    async setCommissionGroups(userId: string, request: SetCommissionGroupsToUserRequest): Promise<void> {
        return await this.put<void, SetCommissionGroupsToUserRequest>(`/users/${userId}/commission-groups`, request);
    }

    /**
     * Get conversions api token for the current authenticated user.
     *
     * - Request: /
     * - Response: GetUserConversionsApiTokenResponse
     */
    async getConversionApiToken(): Promise<GetUserConversionsApiTokenResponse> {
        return await this.get<GetUserConversionsApiTokenResponse, undefined>("/users/conversions-api-token");
    }

    /**
     * Regenerate conversions api token for the current authenticated user.
     *
     * - Request: /
     * - Response: GetUserConversionsApiTokenResponse
     */
    async regenerateConversionApiToken(): Promise<GetUserConversionsApiTokenResponse> {
        return await this.post<GetUserConversionsApiTokenResponse, undefined>(
            "/users/conversions-api-token/regenerate"
        );
    }

    /**
     * Get conversions api token for a specific user (admin only).
     *
     * Tenant-scoped: the backend route lives under the {tenant} prefix so
     * tenant() can scope the user lookup. The token value itself is stored on
     * the central User model.
     *
     * - Request: /
     * - Response: GetUserConversionsApiTokenResponse
     */
    async getConversionApiTokenForUser(userId: string): Promise<GetUserConversionsApiTokenResponse> {
        return await this.get<GetUserConversionsApiTokenResponse, undefined>(`/users/conversions-api-token/${userId}`);
    }

    /**
     * Regenerate conversions api token for a specific user (admin only).
     *
     * - Request: /
     * - Response: GetUserConversionsApiTokenResponse
     */
    async regenerateConversionApiTokenForUser(userId: string): Promise<GetUserConversionsApiTokenResponse> {
        return await this.post<GetUserConversionsApiTokenResponse, undefined>(
            `/users/conversions-api-token/${userId}/regenerate`
        );
    }
}

export const userApi = new UserApi();
